import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Plus, Search } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  useVbsCalculationCreateMutation,
  useVbsCalculationListQuery,
} from "@/services/backend/vbs/calculations/api/calculation-api";
import { v4 } from "uuid";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { ProcessTopic } from "@/services/backend/process-topic/components/process-topic";
import { CopyCalculationButton } from "@/services/backend/vbs/calculations/components/copy-calculation-button";
import { ReactNode, useState } from "react";
import { FilterMode } from "@/shared/lib/filter/filter";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { BranchSelectFrom } from "@/services/backend/company/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import { DocumentState } from "@/services/backend/documents/types/state";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";

interface CalculationRef {
  id: string;
  processId: string | null;
}

interface Props {
  processId: string;
  onCreated: (calculationId: string) => void;
  renderCalculationLink: (
    calculation: CalculationRef,
    children: ReactNode,
  ) => ReactNode;
  trigger?: ReactNode;
}

export function CreateCalculationDialog({
  processId,
  onCreated,
  renderCalculationLink,
  trigger,
}: Props) {
  const [open, setOpen] = useState(false);

  const closeDialogAndNavigate = (calculationId: string) => {
    setOpen(false);
    onCreated(calculationId);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm">
            <Plus />
            <span>{t("Kalkulation")}</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] min-w-[80vw] overflow-y-scroll">
        <DialogHeader>
          <DialogTitle>{t("Neue Kalkulation erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Kalkulationen können entweder komplett neu oder auf Basis einer alten Kalkulation erstellt werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="manual">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual">{t("Neu Anlegen")}</TabsTrigger>
            <TabsTrigger value="from-old">
              {t("Alte Kalkulationen")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="manual">
            <CreateManually
              processId={processId}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
          <TabsContent value="from-old">
            <CreateFromOld
              processId={processId}
              onCreated={closeDialogAndNavigate}
              renderCalculationLink={renderCalculationLink}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function CreateManually({
  processId,
  onCreated,
}: {
  processId: string;
  onCreated: (calculationId: string) => void;
}) {
  const [id, setId] = useState(v4());
  const [create, { isLoading, error, isSuccess, reset }] =
    useVbsCalculationCreateMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  if (isSuccess) {
    onCreated(id);
    reset();
    setId(v4());
  }

  const disabled =
    !process ||
    !process.customerId ||
    isLoading ||
    branchId === "" ||
    serviceObjectId === "";

  const onClick = () => {
    if (disabled) {
      return;
    }

    create({
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Neu Anlegen")}</CardTitle>
        <CardDescription>
          {t(
            "Erstellt eine Kalkulation mit den Basisdaten des Prozesses und des Kunden.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {branches.length !== 1 && (
          <BranchSelectFrom
            branchIds={branches}
            value={branchId}
            onValueChange={setBranchId}
          />
        )}
        {serviceObjects.length !== 1 && (
          <ServiceObjectSelectFrom
            serviceObjectIds={serviceObjects}
            customerId={process?.customerId}
            value={serviceObjectId}
            onValueChange={setServiceObjectId}
          />
        )}
        <RTKQueryErrorAlert error={error} />
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={onClick} disabled={disabled}>
          {t("Erstellen")}
        </Button>
      </CardFooter>
    </Card>
  );
}

function CreateFromOld({
  processId,
  onCreated,
  renderCalculationLink,
}: {
  processId: string;
  onCreated: (calculationId: string) => void;
  renderCalculationLink: (
    calculation: CalculationRef,
    children: ReactNode,
  ) => ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Auf Basis alter Kalkulation")}</CardTitle>
        <CardDescription>
          {t(
            "Kopiert die Daten der alten Kalkulation. Es werden nur Kalkulationen von abgeschlossenen Vorgängen angezeigt.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <OldCalculationsTable
          processId={processId}
          onCreated={onCreated}
          renderCalculationLink={renderCalculationLink}
        />
      </CardContent>
    </Card>
  );
}

function OldCalculationsTable(props: {
  processId: string;
  onCreated: (calculationId: string) => void;
  renderCalculationLink: (
    calculation: CalculationRef,
    children: ReactNode,
  ) => ReactNode;
}) {
  const { processId, onCreated, renderCalculationLink } = props;
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const {
    data: list,
    isLoading,
    error,
  } = useVbsCalculationListQuery({
    customer: {
      active: true,
      values: [process?.customerId ?? ""],
    },
    owner: {
      active: true,
      mode: FilterMode.IsNull,
    },
    state: {
      active: false,
      values: [DocumentState.Immutable],
    },
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Status")}</TableHead>
            <TableHead>{t("Datum")}</TableHead>
            <TableHead className="w-full">{t("Vorgang Thema")}</TableHead>
            <TableHead>{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Status")}</TableHead>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead className="w-full">{t("Vorgang Thema")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list?.items?.map((calculation) => (
          <TableRow key={calculation.id}>
            <TableCell>
              <ReferenceNumber referenceNumber={calculation.referenceNumber} />
            </TableCell>
            <TableCell>
              <DocumentStateBadge document={calculation} />
            </TableCell>
            <TableCell>
              {new Date(calculation.createdAt).toLocaleDateString()}
            </TableCell>
            <TableCell>
              <ProcessTopic processId={processId} />
            </TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <NavigateToCalculation
                  currentProcessId={processId}
                  calculation={calculation}
                  renderCalculationLink={renderCalculationLink}
                />
                <CopyCalculationButton
                  calculationId={calculation.id}
                  processId={processId}
                  onCopy={onCreated}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function NavigateToCalculation(props: {
  currentProcessId: string;
  calculation: CalculationRef;
  renderCalculationLink: (
    calculation: CalculationRef,
    children: ReactNode,
  ) => ReactNode;
}) {
  const { currentProcessId, calculation, renderCalculationLink } = props;

  if (!calculation.processId) {
    return null;
  }

  // If the calculation is part of the same process,
  // simply navigate to the calculation
  if (currentProcessId === calculation.processId) {
    return renderCalculationLink(
      calculation,
      <Button variant="ghost">
        <Search className="h-4 w-4" />
      </Button>,
    );
  }

  // If the calculation is part of a different process,
  // show a warning and then navigate to the calculation
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost">
          <Search className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Achtung")}</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {t(
            "Diese Kalkulation ist Teil eines anderen Vorgangs. Sie müssen nachdem Sie die Kalkulation angesehen haben zum aktuellen Vorgang zurückkehren.",
          )}
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          {renderCalculationLink(
            calculation,
            <Button>{t("Vorgang wechseln")}</Button>,
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
